"use client";

import { useMemo } from "react";
import { useSelector } from "react-redux";

import type {
  iUserAccessV2,
  OrganizationRoleId,
  StoredUserAccess,
} from "@sdk/access/access.models";
import {
  buildUserAccessFromRoles,
  createOwnerAccessV2,
  isValidUserAccessV1,
  isValidUserAccessV2,
  ORGANIZATION_ROLE_IDS,
  ORGANIZATION_ROLES,
} from "@sdk/access/access.models";
import { adaptAccessV1ToV2 } from "@sdk/access/access-v1-adapter";
import { formatBrandAccessSummary } from "@sdk/access/access-role-templates";
import { selectAllBrands } from "@/store/modules/brands/brands.selectors";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const CRM_ORG_ROLES: OrganizationRoleId[] = [
  "travel_agent",
  "brand_manager",
  "bookings_team",
];

export function createDefaultUserAccessV2(): iUserAccessV2 {
  return buildUserAccessFromRoles(["travel_agent"], []);
}

export function createInviteDefaultAccessV2(
  activeBrandIds: string[]
): iUserAccessV2 {
  const brandIds =
    activeBrandIds.length === 1 ? [...activeBrandIds] : [];
  return buildUserAccessFromRoles(["travel_agent"], brandIds);
}

export function resolveAccessForEditor(
  access: StoredUserAccess | null | undefined
): iUserAccessV2 {
  if (access && isValidUserAccessV2(access)) {
    return {
      org: {
        isOwner: access.org.isOwner,
        roles: [...access.org.roles],
        capabilities: [...access.org.capabilities],
      },
      brandIds: [...access.brandIds],
      version: 2,
    };
  }

  if (access && isValidUserAccessV1(access)) {
    return adaptAccessV1ToV2(access);
  }

  return createDefaultUserAccessV2();
}

export function accessNeedsBrandAssignment(access: iUserAccessV2): boolean {
  if (access.org.isOwner) return false;
  return access.org.roles.some((role) => CRM_ORG_ROLES.includes(role));
}

interface UserAccessEditorProps {
  value: iUserAccessV2;
  onChange: (access: iUserAccessV2) => void;
  disabled?: boolean;
}

export function UserAccessEditor({
  value,
  onChange,
  disabled = false,
}: UserAccessEditorProps) {
  const brands = useSelector(selectAllBrands);
  const activeBrands = useMemo(
    () => brands.filter((b) => b.id && b.isDisabled !== true),
    [brands]
  );

  const brandNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const brand of activeBrands) {
      if (brand.id) map[brand.id] = brand.name;
    }
    return map;
  }, [activeBrands]);

  const selectedBrandIds = new Set(value.brandIds);

  function setOwner(isOwner: boolean) {
    if (isOwner) {
      onChange(createOwnerAccessV2());
      return;
    }
    onChange(createDefaultUserAccessV2());
  }

  function toggleRole(role: OrganizationRoleId, checked: boolean) {
    const nextRoles = checked
      ? [...new Set([...value.org.roles, role])]
      : value.org.roles.filter((r) => r !== role);
    onChange(buildUserAccessFromRoles(nextRoles, value.brandIds));
  }

  function toggleBrand(brandId: string, checked: boolean) {
    const nextBrandIds = checked
      ? [...new Set([...value.brandIds, brandId])]
      : value.brandIds.filter((id) => id !== brandId);
    onChange(buildUserAccessFromRoles(value.org.roles, nextBrandIds));
  }

  const showBrandWarning =
    !value.org.isOwner &&
    accessNeedsBrandAssignment(value) &&
    value.brandIds.length === 0;

  const preview = formatBrandAccessSummary(value, brandNameById);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Checkbox
          id="access-owner"
          checked={value.org.isOwner}
          disabled={disabled}
          onCheckedChange={(checked) => setOwner(checked === true)}
        />
        <Label htmlFor="access-owner">Organization owner (full access)</Label>
      </div>

      {!value.org.isOwner && (
        <>
          <div className="space-y-3">
            <Label>Organization roles</Label>
            <div className="space-y-2 rounded-md border p-3">
              {ORGANIZATION_ROLE_IDS.map((roleId) => {
                const role = ORGANIZATION_ROLES[roleId];
                return (
                  <div key={roleId} className="flex items-start gap-2">
                    <Checkbox
                      id={`access-role-${roleId}`}
                      checked={value.org.roles.includes(roleId)}
                      disabled={disabled}
                      onCheckedChange={(checked) =>
                        toggleRole(roleId, checked === true)
                      }
                    />
                    <div className="space-y-0.5 leading-none">
                      <Label
                        htmlFor={`access-role-${roleId}`}
                        className="font-medium"
                      >
                        {role.label}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {role.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-3">
            <Label>Brand access (CRM)</Label>
            {activeBrands.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No active brands in this workspace.
              </p>
            ) : (
              <div className="space-y-2 rounded-md border p-3">
                {activeBrands.map((brand) =>
                  brand.id ? (
                    <div key={brand.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`access-brand-${brand.id}`}
                        checked={selectedBrandIds.has(brand.id)}
                        disabled={disabled}
                        onCheckedChange={(checked) =>
                          toggleBrand(brand.id!, checked === true)
                        }
                      />
                      <Label htmlFor={`access-brand-${brand.id}`}>
                        {brand.name}
                      </Label>
                    </div>
                  ) : null
                )}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Required for Travel Agent, Brand Manager, and Booking Team CRM
              pages.
            </p>
          </div>
        </>
      )}

      {showBrandWarning && (
        <p className="text-xs text-amber-600 dark:text-amber-500">
          Selected roles typically need at least one brand for CRM access.
        </p>
      )}

      {!value.org.isOwner && (
        <p className="text-xs text-muted-foreground">Preview: {preview}</p>
      )}
    </div>
  );
}
